import clsx from "clsx";
import { Gauge, Truck } from "lucide-react";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";

import type { CurvePhase, DeployedUnit, LogisticsMaintenance, MaintenanceFamily, MaintenancePhase, MaterielFamily, UnitCapops } from "../../api/types";
import { Checkbox } from "../../components/Checkbox";
import { Dialog } from "../../components/Dialog";
import { PanelGroup } from "../../components/panel";
import { Select } from "../../components/Select";
import { Badge, inputSmClass, Segmented } from "../../components/ui";
import { echelonName } from "../../echelon";
import { HelpButton } from "../../help/HelpButton";
import { unitDesignation } from "../../i18n/designation";
import { formatNumber, k, t } from "../../i18n/i18n";
import {
  capopsSentence,
  colorTone,
  CURVE_PHASES,
  dtoColor,
  familyLabel,
  GREEN_ABOVE,
  maintenanceColor,
  percentText,
  REQUIRED_PHASES,
} from "../../maintenance";
import { CapopsStatusIcon, DTO_STATUS, DtoStatusIcon, TONE_TEXT } from "./DtoCurve";
import { type CapopsRow, HeadRow, limitNote, PhaseBand } from "./PhaseTables";

/** Tableau de bord de la maintenance (demande d'Alexis du 04/11/2026 : « une vue générale […] avec toutes les unités,
 *  les graphes de CAPOPS et DTO pour tous les matériels majeurs, pour tous les T de la manœuvre ») : grande fenêtre
 *  au-dessus de la carte, ouverte par le bouton « Tableau de bord » de la vue CAPOPS / DTO, refermée par Échap, la
 *  croix ou un clic hors d'elle. Trois parties, la CAPOPS d'abord : synthèse par temps (CAPOPS la plus basse, unités à
 *  70 % ou moins, parcs sous la DTO requise) ; « CAPOPS des unités » de T0 à T3, en barres aux seuils de la CAPOPS
 *  (repère à 70 %), dans l'ordre de bataille ou les plus affaiblies d'abord, filtrées par échelon ; « DTO par parc »
 *  de T0 à T3, en barres colorées face à la DTO requise du parc (repère : la requise du temps). Un clic sur une unité
 *  ouvre sa fiche et centre la carte (`onOpenUnit`), sur un parc son réglage dans la vue (`onOpenFamily`). Rien n'est
 *  recalculé : lecture de `Logistics.maintenance`, la même que la vue. Graphes en SVG et CSS, sans dépendance. */

/** Colonnes : nom, puis un temps par colonne (barres plus larges que celles de la vue). */
const CAPOPS_COLUMNS = "grid grid-cols-[minmax(6rem,1.4fr)_repeat(4,minmax(3rem,1fr))] gap-x-2 sm:gap-x-3";
const DTO_COLUMNS = "grid grid-cols-[minmax(5.5rem,1.4fr)_repeat(4,minmax(3rem,1fr))] gap-x-2 sm:gap-x-3";

const FILL = { ok: "bg-ok", warn: "bg-warn", danger: "bg-danger", neutral: "bg-ink-muted" } as const;

export type DashboardSort = "orbat" | "weakest";

const SORTS: { value: DashboardSort; label: string }[] = [
  { value: "orbat", label: k("Ordre de bataille") },
  { value: "weakest", label: k("CAPOPS la plus faible") },
];

/** Barre horizontale de 0 à 100 % : remplissage au ton de l'état, `marker` en trait vertical (seuil de la CAPOPS ou
 *  DTO requise du temps). Décorative : la valeur est écrite à côté. */
function Bar({ value, tone, marker }: { value: number; tone: keyof typeof FILL; marker?: number | null }) {
  const width = Math.max(0, Math.min(100, value));
  return (
    <span aria-hidden className="relative block h-1.5 w-full rounded-full bg-line">
      <span
        className={clsx("absolute inset-y-0 left-0 rounded-full transition-[width,background-color] duration-(--gx-enter)", FILL[tone])}
        style={{ width: `${width}%` }}
      />
      {marker != null && <span className="absolute -inset-y-1 w-0.5 -translate-x-1/2 rounded-full bg-ink-soft" style={{ left: `${Math.max(0, Math.min(100, marker))}%` }} />}
    </span>
  );
}

/** CAPOPS d'une unité à un temps : valeur et icône d'état, puis la barre (repère au seuil de 70 %). */
function CapopsCell({ capops, phase }: { capops: UnitCapops | undefined; phase: CurvePhase }) {
  if (!capops)
    return (
      <span className="text-right text-meta text-ink-muted" title={t("{phase} : non posée", { phase })}>
        —
      </span>
    );
  const tone = colorTone(maintenanceColor(capops.capops));
  return (
    <span className="flex min-w-0 flex-col gap-1" title={t("{phase} : {text}", { phase, text: capopsSentence(capops) })}>
      <span className={clsx("flex items-center justify-end gap-1 text-meta font-medium tabular-nums", TONE_TEXT[tone])}>
        <CapopsStatusIcon percent={capops.capops} size={11} />
        {percentText(capops.capops)}
      </span>
      <Bar value={capops.capops} tone={tone} marker={GREEN_ABOVE} />
    </span>
  );
}

/** DTO d'un parc à un temps : valeur et icône face à la DTO requise, puis la barre (repère : la requise ; T0, DTO de
 *  départ, neutre et sans repère). */
function DtoCell({ family, phase }: { family: MaintenanceFamily; phase: CurvePhase }) {
  const value = family.dto[phase];
  const required = phase === "T0" ? null : family.required[phase];
  const color = dtoColor(value, required);
  const tone = colorTone(color);
  const title =
    required === null
      ? t("{phase} : DTO de départ {n} %", { phase, n: Math.round(value) })
      : t("{phase} : {n} %, requise {required} % · {status}", {
          phase,
          n: Math.round(value),
          required: Math.round(required),
          status: color ? t(DTO_STATUS[color]) : "",
        });
  return (
    <span className="flex min-w-0 flex-col gap-1" title={title}>
      <span className={clsx("flex items-center justify-end gap-1 text-meta font-medium tabular-nums", required === null ? "text-ink-soft" : TONE_TEXT[tone])}>
        {required !== null && <DtoStatusIcon percent={value} required={required} size={11} />}
        {percentText(value)}
      </span>
      <Bar value={value} tone={tone} marker={required} />
    </span>
  );
}

// --- Lignes de l'ordre de bataille ---------------------------------------------------------------------------------

/** Ligne du tableau : une unité estimée, ou une formation supérieure qui n'a pas de CAPOPS (titre de groupe). */
export type DashboardRow =
  | { kind: "unit"; key: string; row: CapopsRow; depth: number; echelon: string | null; context: string | null }
  | { kind: "group"; key: string; name: string; depth: number; echelon: string | null };

/** Unité affaiblie à au moins un temps (CAPOPS orange ou rouge). */
const weak = (row: CapopsRow) => REQUIRED_PHASES.some((p) => {
  const c = row.phases[p];
  return c != null && maintenanceColor(c.capops) !== "green";
});

/** Lignes du tableau de bord. `rows` : CAPOPS des unités (`capopsRows`, les plus affaiblies d'abord) ; `units` :
 *  dispositif (parents, échelons, ordre de l'ordre de bataille). En ordre de bataille, chaque unité sous sa formation
 *  supérieure (titre de groupe quand celle-ci n'a pas de CAPOPS, nommée par le dispositif ou par son code) ; sinon
 *  une seule liste, les plus affaiblies d'abord, la formation supérieure en mention. Filtres : échelon (`all` : tous),
 *  unités à 70 % ou moins seulement. */
export function dashboardRows(
  rows: CapopsRow[],
  units: DeployedUnit[],
  { sort, echelon, weakOnly }: { sort: DashboardSort; echelon: string; weakOnly: boolean },
): DashboardRow[] {
  const ami = units.filter((u) => u.side === "AMI");
  const byCode = new Map(ami.map((u) => [u.code, u]));
  const byKey = new Map(ami.map((u) => [u.key, u]));
  const order = new Map(ami.map((u, i) => [u.code, i]));
  const nameOf = (code: string) => {
    const u = byCode.get(code);
    return unitDesignation(u ? (u.display_name ?? u.designation) : code);
  };
  const kept = rows.filter((r) => (echelon === "all" || byKey.get(r.key)?.echelon === echelon) && (!weakOnly || weak(r)));
  if (sort === "weakest") {
    return kept.map((row) => {
      const parent = byKey.get(row.key)?.parent;
      return { kind: "unit", key: row.key, row, depth: 0, echelon: byKey.get(row.key)?.echelon ?? null, context: parent ? nameOf(parent) : null };
    });
  }
  // Ordre de bataille : nœuds = unités gardées et leurs formations supérieures (jusqu'à la racine).
  const rowByCode = new Map<string, CapopsRow>();
  const parentOf = new Map<string, string | null>();
  const add = (code: string, parent: string | null) => {
    if (parentOf.has(code)) return;
    parentOf.set(code, parent && parent !== code ? parent : null);
  };
  for (const row of kept) {
    const unit = byKey.get(row.key);
    const code = unit?.code ?? row.key;
    rowByCode.set(code, row);
    add(code, unit?.parent ?? null);
    let parent = unit?.parent ?? null;
    const seen = new Set([code]);
    while (parent && !seen.has(parent)) {
      seen.add(parent);
      const above = byCode.get(parent);
      add(parent, above?.parent ?? null);
      parent = above?.parent ?? null;
    }
  }
  const children = new Map<string | null, string[]>();
  for (const [code, parent] of parentOf) {
    const at = parent && parentOf.has(parent) ? parent : null;
    children.set(at, [...(children.get(at) ?? []), code]);
  }
  const rank = (code: string) => order.get(code) ?? Number.MAX_SAFE_INTEGER;
  const out: DashboardRow[] = [];
  const walk = (parent: string | null, depth: number) => {
    const list = [...(children.get(parent) ?? [])].sort((a, b) => rank(a) - rank(b) || nameOf(a).localeCompare(nameOf(b)));
    for (const code of list) {
      const row = rowByCode.get(code);
      const unit = byCode.get(code);
      if (row) out.push({ kind: "unit", key: row.key, row, depth, echelon: unit?.echelon ?? null, context: null });
      else out.push({ kind: "group", key: `group:${code}`, name: nameOf(code), depth, echelon: unit?.echelon ?? null });
      walk(code, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** Synthèse d'un temps : CAPOPS la plus basse (et son unité), unités à 70 % ou moins, parcs sous la DTO requise. */
export function phaseSummary(rows: CapopsRow[], families: MaintenanceFamily[], phase: MaintenancePhase) {
  const placed = rows.flatMap((r) => (r.phases[phase] ? [{ row: r, capops: r.phases[phase]! }] : []));
  const weakest = placed.reduce<(typeof placed)[number] | undefined>((a, b) => (!a || b.capops.capops < a.capops.capops ? b : a), undefined);
  const weakened = placed.filter((u) => Math.round(u.capops.capops) <= GREEN_ABOVE).length;
  const below = families.filter((f) => Math.round(f.dto[phase]) < Math.round(f.required[phase]));
  return { weakest, weakened, placed: placed.length, below };
}

// --- Fenêtre --------------------------------------------------------------------------------------------------------

function PhaseCard({
  phase,
  current,
  hours,
  summary,
  fleets,
}: {
  phase: MaintenancePhase;
  current: boolean;
  hours: number;
  summary: ReturnType<typeof phaseSummary>;
  fleets: number;
}) {
  const { weakest, weakened, placed, below } = summary;
  const tone = weakest ? colorTone(maintenanceColor(weakest.capops.capops)) : "neutral";
  return (
    <div className={clsx("min-w-0 rounded-lg border px-3 py-2.5", current ? "border-line-strong bg-surface" : "border-transparent bg-canvas")}>
      <div className="flex min-h-6 items-center gap-2">
        <span className="text-ui font-semibold text-ink tabular-nums">{phase}</span>
        <span className="text-meta text-ink-muted tabular-nums">{t("{hours} h", { hours: formatNumber(Math.round(hours)) })}</span>
        <span className="flex-1" />
        {current && <Badge>{t("Temps affiché")}</Badge>}
      </div>
      <div className="mt-1 text-meta leading-snug text-ink-muted">{t("CAPOPS la plus basse")}</div>
      {weakest ? (
        <div className="flex min-w-0 items-baseline gap-2">
          <span className={clsx("inline-flex items-center gap-1 text-stat leading-tight font-semibold tabular-nums", TONE_TEXT[tone])}>
            <CapopsStatusIcon percent={weakest.capops.capops} size={14} />
            {percentText(weakest.capops.capops)}
          </span>
          <span className="min-w-0 text-ui leading-snug break-words text-ink-soft">{unitDesignation(weakest.row.name)}</span>
        </div>
      ) : (
        <div className="text-stat leading-tight text-ink-muted">—</div>
      )}
      <ul className="mt-2 space-y-1 border-t border-line pt-2 text-meta leading-snug">
        <li className="flex items-center gap-2">
          <Gauge size={12} aria-hidden className="shrink-0 text-ink-muted" />
          <span className="min-w-0 flex-1 text-ink-soft">{t("Unités à 70 % ou moins")}</span>
          <span className={clsx("font-medium tabular-nums", weakened ? "text-warn" : "text-ink")}>
            {t("{n} sur {total}", { n: formatNumber(weakened), total: formatNumber(placed) })}
          </span>
        </li>
        <li className="flex items-center gap-2">
          <Truck size={12} aria-hidden className="shrink-0 text-ink-muted" />
          <span className="min-w-0 flex-1 text-ink-soft">{t("Parcs sous la DTO requise")}</span>
          <span
            className={clsx("font-medium tabular-nums", below.length ? "text-warn" : "text-ink")}
            title={below.length ? below.map((f) => familyLabel(f.family)).join(t(", ")) : undefined}
          >
            {t("{n} sur {total}", { n: formatNumber(below.length), total: formatNumber(fleets) })}
          </span>
        </li>
      </ul>
    </div>
  );
}

/** Tableau de bord de la maintenance (à entourer de `Presence`). `rows` : CAPOPS des unités (`capopsRows`) ;
 *  `phase` : temps affiché sur la carte (mis en valeur, jamais changé ici). */
export function MaintenanceDashboard({
  m,
  rows,
  units,
  phase,
  onClose,
  onOpenUnit,
  onOpenFamily,
}: {
  m: LogisticsMaintenance;
  rows: CapopsRow[];
  units: DeployedUnit[];
  phase: MaintenancePhase;
  onClose: () => void;
  onOpenUnit?: (key: string, phases: CapopsRow["phases"]) => void;
  onOpenFamily?: (family: MaterielFamily) => void;
}) {
  const [sort, setSort] = useState<DashboardSort>("orbat");
  const [echelon, setEchelon] = useState("all");
  const [weakOnly, setWeakOnly] = useState(false);
  const echelons = useMemo(() => {
    const keys = new Set(rows.map((r) => r.key));
    const seen: string[] = [];
    for (const u of units) if (u.side === "AMI" && keys.has(u.key) && u.echelon && !seen.includes(u.echelon)) seen.push(u.echelon);
    return seen;
  }, [rows, units]);
  const shown = useMemo(() => dashboardRows(rows, units, { sort, echelon, weakOnly }), [rows, units, sort, echelon, weakOnly]);
  const unitCount = shown.filter((r) => r.kind === "unit").length;
  const below = m.families.filter((f) => f.below).length;
  const dialog = (
    <Dialog
      title={t("Situation CAPOPS / DTO")}
      subtitle={t("CAPOPS des unités et DTO des parcs à tous les temps · en direct depuis la carte, temps affiché {phase}", { phase })}
      width={1180}
      onClose={onClose}
      actions={<HelpButton screen="maintenance" topic="maintenance.tableau" buttonSize="sm" size={16} />}
    >
      {/* Hauteur minimale : un filtre qui raccourcit la liste ne fait pas sauter la fenêtre (centrée) ; la note de bas
          reste en bas. */}
      <div data-maintenance-dashboard="" className="flex min-h-[min(36rem,75vh)] flex-col">
        <PanelGroup title={t("Synthèse par temps")}>
          <div className="grid gap-2 pb-1 sm:grid-cols-3">
            {REQUIRED_PHASES.map((p) => (
              <PhaseCard key={p} phase={p} current={p === phase} hours={m.hours[p]} summary={phaseSummary(rows, m.families, p)} fleets={m.families.length} />
            ))}
          </div>
        </PanelGroup>

        <div className="grid lg:grid-cols-2">
          <PanelGroup title={t("CAPOPS des unités")} count={unitCount} className="lg:border-r lg:border-b-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-2 pb-2">
              <Segmented
                ariaLabel={t("Ordre des unités")}
                size="sm"
                role="tab"
                fill="natural"
                slide
                value={sort}
                onChange={setSort}
                options={SORTS.map((s) => ({ value: s.value, label: t(s.label) }))}
              />
              {echelons.length > 1 && (
                <div className="w-48 max-w-full">
                  <Select className={inputSmClass} aria-label={t("Échelon")} value={echelon} onChange={(e) => setEchelon(e.target.value)}>
                    <option value="all">{t("Tous les échelons")}</option>
                    {echelons.map((e) => (
                      <option key={e} value={e}>
                        {echelonName(e) ?? e}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              <Checkbox checked={weakOnly} onChange={setWeakOnly} label={t("70 % ou moins seulement")} />
            </div>
            {shown.length ? (
              <div className="relative">
                <PhaseBand columns={CAPOPS_COLUMNS} index={CURVE_PHASES.indexOf(phase)} />
                <HeadRow columns={CAPOPS_COLUMNS} label={t("Unité")} phases={CURVE_PHASES} phase={phase} />
                <ul className="relative">
                  {shown.map((r) => (
                    <DashboardLine key={r.key} line={r} phase={phase} onOpen={onOpenUnit} />
                  ))}
                </ul>
              </div>
            ) : (
              <p className="px-2 py-3 text-ui text-ink-muted">{t("Aucune unité ne répond à ces filtres.")}</p>
            )}
            <p className="flex items-center gap-1.5 px-2 pt-2 text-meta leading-snug text-ink-muted">
              <span aria-hidden className="h-2.5 w-0.5 shrink-0 rounded-full bg-ink-soft" />
              {t("Repère : seuil de 70 % ; vert au-dessus, orange de 51 à 70 %, rouge à 50 % ou moins.")}
            </p>
          </PanelGroup>

          <PanelGroup
            title={t("DTO par parc")}
            count={m.families.length}
            actions={below ? <Badge tone="warn">{t("{n} sous la DTO requise", { n: formatNumber(below) })}</Badge> : undefined}
          >
            <div className="relative">
              <PhaseBand columns={DTO_COLUMNS} index={CURVE_PHASES.indexOf(phase)} />
              <HeadRow columns={DTO_COLUMNS} label={t("Parc")} phases={CURVE_PHASES} phase={phase} />
              <ul className="relative">
                {m.families.map((f) => (
                  <li key={f.family}>
                    <button
                      type="button"
                      data-dashboard-family={f.family}
                      onClick={() => onOpenFamily?.(f.family)}
                      title={t("Régler la DTO requise de ce parc")}
                      className={clsx(DTO_COLUMNS, "focus-inset min-h-8 w-full items-center rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-hover")}
                    >
                      <span className="min-w-0">
                        <span className="block text-ui leading-snug font-medium break-words text-ink">{familyLabel(f.family)}</span>
                        <span className="block text-meta leading-snug text-ink-muted tabular-nums">
                          {t("{n} véhicules", { n: formatNumber(Math.round(f.vehicles)) })}
                          {f.below && <span className={TONE_TEXT[colorTone(f.color[f.below])]}> · {t("sous la requise dès {phase}", { phase: f.below })}</span>}
                        </span>
                      </span>
                      {CURVE_PHASES.map((p) => (
                        <DtoCell key={p} family={f} phase={p} />
                      ))}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <p className="flex items-center gap-1.5 px-2 pt-2 text-meta leading-snug text-ink-muted">
              <span aria-hidden className="h-2.5 w-0.5 shrink-0 rounded-full bg-ink-soft" />
              {t("Repère : DTO requise du parc à ce temps ; T0, DTO de départ.")}
            </p>
          </PanelGroup>
        </div>

        <p className="mt-auto border-t border-line px-4 py-3 text-meta leading-relaxed text-ink-muted">
          {t("Un clic sur une unité ouvre sa fiche sur la carte ; sur un parc, le réglage de sa DTO requise.")}
        </p>
      </div>
    </Dialog>
  );
  // Portail : la fenêtre couvre la fenêtre de l'application, pas le panneau de la vue qui l'ouvre (déplacé en glissant
  // à son apparition, il deviendrait le repère de son voile). Sans `document` (rendu statique), la fenêtre seule.
  return typeof document === "undefined" ? dialog : createPortal(dialog, document.body);
}

/** Une ligne : unité (bouton : sa fiche) ou formation supérieure sans CAPOPS (titre de groupe), en retrait selon sa
 *  profondeur dans l'ordre de bataille. */
function DashboardLine({ line, phase, onOpen }: { line: DashboardRow; phase: MaintenancePhase; onOpen?: (key: string, phases: CapopsRow["phases"]) => void }) {
  const indent = { paddingLeft: `${line.depth}rem` };
  if (line.kind === "group")
    return (
      <li className={clsx(CAPOPS_COLUMNS, "min-h-8 items-center px-2 py-1.5")}>
        <span className="min-w-0" style={indent}>
          <span className="block text-ui leading-snug font-medium break-words text-ink-muted">{line.name}</span>
          {line.echelon && <span className="block text-meta leading-snug text-ink-muted">{echelonName(line.echelon)}</span>}
        </span>
      </li>
    );
  const { row } = line;
  const name = unitDesignation(row.name);
  const note = limitNote(row, phase);
  const meta = [line.context, note].filter(Boolean).join(" · ");
  const summary = CURVE_PHASES.map((p) => {
    const c = row.phases[p];
    return c ? t("{phase} : {text}", { phase: p, text: capopsSentence(c) }) : t("{phase} : non posée", { phase: p });
  }).join(t(" ; "));
  const body = (
    <>
      <span className="min-w-0" style={indent}>
        <span className="block text-ui leading-snug font-semibold break-words text-ink">{name}</span>
        {meta && <span className="block text-meta leading-snug break-words text-ink-muted">{meta}</span>}
      </span>
      {CURVE_PHASES.map((p) => (
        <CapopsCell key={p} capops={row.phases[p]} phase={p} />
      ))}
    </>
  );
  return (
    <li>
      {onOpen ? (
        <button
          type="button"
          data-dashboard-unit={row.key}
          onClick={() => onOpen(row.key, row.phases)}
          title={t("Ouvrir la fiche de l'unité")}
          aria-label={t("{unit} — {summary}", { unit: name, summary })}
          className={clsx(CAPOPS_COLUMNS, "focus-inset min-h-8 w-full items-center rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-hover")}
        >
          {body}
        </button>
      ) : (
        <div className={clsx(CAPOPS_COLUMNS, "min-h-8 items-center px-2 py-1.5")}>{body}</div>
      )}
    </li>
  );
}
